import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Alert, Link, Stack, Typography, styled } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { AUTH, CLOUD_FUNCTIONS } from '../../auth/FirebaseContext';
import { SignInMethod, useLoginFlow } from '../../auth/LoginProvider';
import { useAuthContext } from '../../auth/useAuthContext';
import { RHFTextField } from '../../components/hook-form';
import FormProvider from '../../components/hook-form/FormProvider';
import Iconify from '../../components/iconify/Iconify';
import { MenuPopoverProps } from '../../components/menu-popover';
import { brandConfig } from '../../config';
import { isCustomDomainMode } from '../../utils/customDomainUtils';
import { ContactUsPopover } from '../../layouts/dashboard/header/ContactUsPopover';
import { cloud_upsertUserCredential, cloud_updateUserBrand } from '../../utils/mrr/cloudFunctions';
import { validatePhoneNumber } from '../../utils/mrr/phone/phone';
import { EMAIL_REGEX } from '../../utils/mrr/uiConstants';
import { FirebaseError } from 'firebase/app';
import { CheckEnvironment, SupportedEnvironments } from '../../utils/environment';

interface VerifyCredentialFormFieldValues {
  emailOrPhone: string;
}

const VerifyCredentialFormScheme = Yup.object().shape({
  emailOrPhone: Yup.string()
    .required('Phone number or email required')
    .test(
      'valid-email-or-phone', // test name
      'The phone number or email must be valid.',
      (value) => {
        const validAsEmail = value.match(EMAIL_REGEX) !== null;
        const phoneValidation = validatePhoneNumber(value, 'US');
        const validCredential = validAsEmail || phoneValidation.success;
        return validCredential;
      }
    ),
});

type VerifyCredentialFormProps = {
  next(method: SignInMethod): void;
};

export function VerifyCredentialForm({ next }: VerifyCredentialFormProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();
  const { emailToVerify, setEmailToVerify, setPhoneToVerify, alertFromOOBAction, setSignInMethod } =
    useLoginFlow();

  useEffect(() => {
    if (alertFromOOBAction) {
      window.history.replaceState(null, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertFromOOBAction]);

  // Handle automatic navigation after custom token authentication
  useEffect(() => {
    if (isAuthenticated) {
      // Let AuthGuard handle the navigation - no manual navigation needed!
    }
  }, [isAuthenticated]);

  let defaultFormCredential = searchParams.has('credential')
    ? searchParams.get('credential')!
    : emailToVerify;

  // if no 'credential' param was supplied, fall back to 'email' (note that credential could be a phone numberP)
  if (!defaultFormCredential) {
    defaultFormCredential = searchParams.has('email') ? searchParams.get('email')! : '';
  }

  const methods = useForm<VerifyCredentialFormFieldValues>({
    resolver: yupResolver(VerifyCredentialFormScheme),
    defaultValues: { emailOrPhone: defaultFormCredential },
    mode: 'all',
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    trigger,
    setError,
  } = methods;

  useEffect(() => {
    // This will validate the input credential if prefilled by search params
    if (defaultFormCredential !== '') {
      trigger();
    }
  }, [defaultFormCredential, trigger]);


  const onSubmit = handleSubmit(async (data) => {
    try {
      const request = httpsCallable<{ credential: string; brand: string }, Record<string, any>>(
        CLOUD_FUNCTIONS,
        cloud_upsertUserCredential
      );
      const verifcationResponse = await request({
        credential: data.emailOrPhone,
        brand: brandConfig.brandCode,
      });
      const { method, email, phone, success, error, customToken } = verifcationResponse.data;

      if (error) {
        setError('emailOrPhone', { message: 'Please try again.' });
        return;
      }

      if (!success) {
        setError('emailOrPhone', { message: 'Account not found.' });
        return;
      }

      // Handle custom token mode for custom domains
      const customDomainMode = isCustomDomainMode();

      if (customDomainMode) {
        // In custom domain mode, handle both email and phone authentication
        if (method === 'email') {
          setEmailToVerify(email || '');
          setSignInMethod(SignInMethod.Email);
          // User will receive custom token email and click the link
          next(method);
          return;
        }
        if (method === 'phone') {
          // Phone auth supported in custom domain mode, but may fail if domain not whitelisted
          setPhoneToVerify(phone || '');
          setSignInMethod(SignInMethod.Phone);
          next(method);
          return;
        }
      }

      // Legacy: Handle direct custom token in response (should not happen with new flow)
      if (customToken && !customDomainMode) {
        // Custom token received but not in custom domain mode - unexpected scenario
        console.warn('Received custom token outside of custom domain mode');
      }

      // Standard email/phone verification flow
      if (method === 'email') {
        setEmailToVerify(email || '');
      } else {
        setPhoneToVerify(phone || '');
      }

      const emailBasedOTP = method === 'email';
      if (emailBasedOTP) {
        setSignInMethod(SignInMethod.Email);
      } else {
        setSignInMethod(SignInMethod.Phone);
      }
      next(method);
    } catch (caught: any) {
      // Check for custom domain not supported error
      if (
        caught?.code === 'functions/failed-precondition' &&
        caught?.message?.includes('Email links not supported')
      ) {
        setError('emailOrPhone', { message: caught.message });
      }
      // Check for phone authentication failures on custom domains
      else if (
        isCustomDomainMode() &&
        (caught?.message?.includes('phone') ||
          caught?.code === 'auth/unauthorized-domain' ||
          (caught?.message?.includes('domain') && caught?.message?.includes('authorized')))
      ) {
        setError('emailOrPhone', {
          message: 'Phone authentication failed. Custom domains must be whitelisted in Firebase.',
        });
      } else {
        setError('emailOrPhone', { message: getErrorText(caught) });
      }
    }
  });
  return (
    <FormProvider name="login_form" id="login_form" methods={methods} onSubmit={onSubmit}>
      <Stack spacing={2.5}>
        <WelcomeAlert greeting={brandConfig.greeting} />
        <StyleInput
          disabled={isSubmitting}
          InputProps={{
            endAdornment: <Iconify color="inherit" icon="eva:person-outline" ml={1} />,
          }}
          name="emailOrPhone"
          label="Phone or email"
          type="text"
          autoFocus
        />

        <Stack direction="column" spacing={1}>
          <LoadingButton
            disabled={isSubmitting || !isValid}
            size="large"
            loading={isSubmitting}
            type="submit"
            variant="contained"
          >
            Continue
          </LoadingButton>

          <HelpLink arrow="top-left" />
        </Stack>
      </Stack>
    </FormProvider>
  );
}

//-----------------

const StyleInput = styled(
  RHFTextField,
  {}
)(({ theme }) => ({
  input: {
    '&:-webkit-autofill': {
      webkitBoxShadow: 'none !important',
      webkitTextFillColor: 'inherit',
      webkitBackgroundClip: 'text !important',
    },
  },
}));

function WelcomeAlert({ greeting }: { greeting: string }) {
  return (
    <Alert severity="info">
      <Stack>
        <Typography variant="h6">{greeting}!</Typography>
        <Typography>Please enter your phone number or email to continue.</Typography>
      </Stack>
    </Alert>
  );
}

export function HelpLink({
  arrow,
  labelText,
}: {
  arrow?: MenuPopoverProps['arrow'];
  labelText?: string;
}) {
  const [open, setOpen] = useState<HTMLElement | null>(null);
  const handleOpenContactUs = (event: React.MouseEvent<HTMLAnchorElement>) => {
    setOpen(event.currentTarget);
  };
  const handleCloseContactUs = () => {
    setOpen(null);
  };

  return (
    <>
      <Link sx={{ cursor: 'pointer' }} onClick={handleOpenContactUs} color="primary">
        {labelText ? labelText : 'Help?'}
      </Link>
      <ContactUsPopover arrow={arrow} onClose={handleCloseContactUs} open={open} />
    </>
  );
}

function getErrorText(e: any) {
  if (e instanceof FirebaseError && e.code === 'functions/invalid-argument') {
    return 'There was a problem with your account. Please contact support.';
  }
  if (e && e.details && e.details.error && e.details.error.message) {
    return e.details.error.message;
  }
  return 'Unable to verify guest.';
}
